import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MD5 from "crypto-js/md5";
import { API_URL } from "./components/config";
import Swal from "sweetalert2"; // Import SweetAlert

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Log API_URL on component mount
  useEffect(() => {
    console.log("🌐 [Login] Component mounted - API_URL:", API_URL);
    console.log("📍 [Login] Current URL:", window.location.href);
    console.log("📍 [Login] Current path:", window.location.pathname);
    console.log("🔍 [Login] Login component is rendering!");
    
    // Add global click listener for debugging
    const handleGlobalClick = (e) => {
      console.log("🌍 [Login] Global click detected:", {
        target: e.target.tagName,
        className: e.target.className,
        id: e.target.id,
        type: e.target.type,
        isButton: e.target.tagName === "BUTTON",
        isSubmit: e.target.type === "submit"
      });
    };
    
    document.addEventListener('click', handleGlobalClick);
    
    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, []);

  const readJsonSafe = async (res) => {
    const text = await res.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return { message: text };
    }
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    
    try {
      const emailValue = String(email || "").trim();
      const hashedPassword = MD5(String(password || "")).toString();

      console.log("🔍 [Login] Login process started");
      console.log("📧 [Login] Email:", emailValue);
      console.log("🔑 [Login] Hash generated:", hashedPassword);
      console.log("🌐 [Login] API URL:", `${API_URL}/users/login/${emailValue}/${hashedPassword}`);
      console.log("📤 [Login] Sending login request...");
      
      const response = await fetch(
        `${API_URL}/users/login/${emailValue}/${hashedPassword}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("📥 [Login] Response received:", {
        status: response.status,
        ok: response.ok,
        statusText: response.statusText
      });

      const result = await readJsonSafe(response);
      console.log("📊 [Login] Response data:", result);
      console.log("🔍 [Login] Response analysis:", {
        isArray: Array.isArray(result),
        length: result?.length,
        type: typeof result,
        keys: result ? Object.keys(result) : null,
        firstItem: result?.[0],
        stringified: JSON.stringify(result, null, 2)
      });

      if (!response.ok) {
        const errorMessage =
          result?.message || `Login request failed (HTTP ${response.status}).`;
        console.error("❌ [Login] HTTP Error:", {
          status: response.status,
          ok: response.ok,
          errorMessage,
          result,
        });
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: errorMessage,
        });
        return;
      }

      if (response.ok && Array.isArray(result) && result.length > 0) {
        console.log("✅ [Login] Login successful - processing user data");
        const user = result[0]?.userid;
        const role = result[0]?.role;
        const username = result[0]?.username;
        
        console.log("👤 [Login] User data extracted:", {
          user,
          role,
          username,
          status: result[0]?.status,
          fullUser: result[0]
        });
        
        if (result[0]?.status === "active") {
          console.log("🟢 [Login] User status is active - setting localStorage");
          
          localStorage.setItem("adminId", user);
          localStorage.setItem("role", role);
          localStorage.setItem("userDetails", JSON.stringify(result[0]));
          localStorage.setItem("userName", username);
          
          console.log("💾 [Login] localStorage set:", {
            adminId: user,
            role: role,
            userName: username
          });
          
          const redirectPath = 
            role === "Human Resources" 
              ? "/HrHome" 
              : role === "Support" || role === "Executive"
                ? "/TechSupportHome"
                : "/heatmap-ride-demand";
          
          console.log("🚀 [Login] Redirecting to:", redirectPath);
          navigate(redirectPath);
        } else {
          console.error("🔴 [Login] User account is inactive");
          Swal.fire({
            icon: "error",
            title: "Account Inactive",
            text: "Your account is not active. Please contact an administrator.",
          });
        }
      } else {
        const errorMessage = "Invalid credentials or user not found.";
        console.error("❌ [Login] Login failed - invalid credentials:", {
          status: response.status,
          ok: response.ok,
          errorMessage,
          result,
          isArray: Array.isArray(result),
          resultLength: result?.length
        });
        Swal.fire({
          icon: "error",
          title: "Login Failed",
          text: errorMessage,
        });
      }
    } catch (error) {
      console.error("💥 [Login] Exception occurred:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred during login. Please try again.",
      });
    } finally {
      console.log("🏁 [Login] Login process completed");
      setLoading(false);
    }
  };

  // Add a simple button click handler for debugging
  const handleButtonClick = () => {
    console.log("🔘 [Login] Button onClick fired!");
    console.log("🔘 [Login] Button state:", {
      loading: loading,
      disabled: loading,
      email: email,
      password: password ? "***" : "empty"
    });
  };

  const handleSubmit = (e) => {
    console.log("🖱️ [Login] handleSubmit called - event type:", e.type);
    console.log("📝 [Login] Event object:", {
      type: e.type,
      defaultPrevented: e.defaultPrevented,
      bubbles: e.bubbles,
      cancelable: e.cancelable,
      timeStamp: e.timeStamp
    });
    
    e.preventDefault();
    console.log("� [Login] preventDefault called");
    
    console.log("��️ [Login] Login button clicked");
    console.log("📝 [Login] Form data:", {
      email: email,
      password: password ? "***" : "empty",
      loading: loading,
      emailLength: email?.length || 0,
      passwordLength: password?.length || 0
    });
    
    if (loading) {
      console.warn("⚠️ [Login] Login already in progress, ignoring click");
      return;
    }
    
    if (!email || !password) {
      console.warn("⚠️ [Login] Missing credentials:", {
        hasEmail: !!email,
        hasPassword: !!password
      });
      return;
    }
    
    // Log the exact values being used
    const emailValue = String(email || "").trim();
    const hashedPassword = MD5(String(password || "")).toString();
    console.log("🔍 [Login] Debug values:", {
      originalEmail: email,
      trimmedEmail: emailValue,
      originalPassword: password ? "***" : "empty",
      hashedPassword: hashedPassword,
      passwordLength: password?.length || 0
    });
    
    console.log("🚀 [Login] About to call fetchUserDetails");
    fetchUserDetails();
    console.log("📞 [Login] fetchUserDetails call completed");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#002966] to-[#001a4d] p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8 space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-[#002966] mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-500">Please sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#ffc000] focus:border-transparent transition duration-200 outline-none"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 block">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#ffc000] focus:border-transparent transition duration-200 outline-none"
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              onClick={handleButtonClick}
              className={`w-full py-3 px-4 rounded-lg text-white font-medium transition duration-200 
                ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#002966] hover:bg-[#001a4d] active:transform active:scale-95"
                }`}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                  Signing In...
                </div>
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Need help?</span>
            </div>
          </div>

          <div className="text-center">
            <button className="text-[#002966] hover:text-[#ffc000] transition duration-200">
              Forgot Password?
            </button>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-gray-200">
          © 2026 XGO. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default Login;
